/**
 * Manual ordering for the things a caregiver arranges: the phrases inside a category, and the
 * categories on the phrase screen (`AUT-02`).
 *
 * **Order is per category.** `Phrase.order` is only ever compared among the phrases of one
 * category — `phraseRepo.byCategory` sorts what the index gives it — so every function here takes
 * one category's rows and never sees the rest of the board. A writer that renumbered across
 * categories would produce tiles in the right sequence for the wrong reason and break the first
 * time a category was deleted.
 *
 * **A move renumbers the run rather than swapping two numbers.** Care mode has been writing
 * `order: 999` on every addition since it was built, so a board can hold several rows claiming the
 * same slot; swapping the two values under a caregiver's finger would then move nothing at all.
 * Renumbering from the sequence the rows are actually in fixes those rows on the way past.
 *
 * Pure, so the arithmetic can be reasoned about without a database.
 */

export interface Ordered {
  readonly id: string;
  readonly order: number;
}

/** One row to write back: everything else about it is unchanged. */
export interface OrderWrite {
  id: string;
  order: number;
}

/** The place a new row takes: after everything already there, so it lands where it was added. */
export function nextOrder(existing: readonly Ordered[]): number {
  return existing.reduce((highest, row) => Math.max(highest, row.order), -1) + 1;
}

/**
 * Move one row one place up or down among its siblings.
 *
 * Returns only the rows whose order actually changes, lowest first — and nothing at all at the ends
 * of the run or for an id that is not there, which is what makes the buttons inert rather than
 * silently destructive.
 */
export function moveOneStep(
  rows: readonly Ordered[],
  id: string,
  direction: 'up' | 'down',
): OrderWrite[] {
  const sequence = [...rows].sort((a, b) => a.order - b.order);

  const from = sequence.findIndex((row) => row.id === id);
  if (from === -1) return [];

  const to = direction === 'up' ? from - 1 : from + 1;
  if (to < 0 || to >= sequence.length) return [];

  const moved = [...sequence];
  const [row] = moved.splice(from, 1);
  if (row === undefined) return [];
  moved.splice(to, 0, row);

  return moved
    .map((entry, index) => ({ id: entry.id, order: index, was: entry.order }))
    .filter((write) => write.order !== write.was)
    .map(({ id: writeId, order }) => ({ id: writeId, order }))
    .sort((a, b) => a.order - b.order);
}
